import { useState, useEffect, useCallback } from 'react'
import * as api from '../services/api'

function EmailViewer({ email, address, onBack }) {
  const [fullEmail, setFullEmail] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showHtml, setShowHtml] = useState(true)
  const [composeMode, setComposeMode] = useState(null) // null | 'reply' | 'replyAll'
  const [composeTo, setComposeTo] = useState('')
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [sendResult, setSendResult] = useState(null)

  const loadEmail = useCallback(async () => {
    try {
      setError(null)
      const response = await api.getEmail(email.message_id, address.email)
      setFullEmail(response)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [email.message_id, address])

  useEffect(() => {
    loadEmail()
  }, [loadEmail])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  // Pick the address a reply should go TO. Prefer explicit Reply-To header
  // (that's where support-ticket flows route per-ticket), else the From header,
  // else the sender field we surfaced in the list view.
  const replyToAddress = (data) => {
    const headers = data.headers || {}
    const extract = (s) => {
      if (!s) return ''
      const m = s.match(/<([^>]+)>/)
      return (m ? m[1] : s).trim()
    }
    return extract(headers['Reply-To']) || extract(headers['From']) || extract(data.sender) || ''
  }

  // "Reply All" = primary + every other address on To/Cc that isn't us.
  const replyAllAddresses = (data, self) => {
    const headers = data.headers || {}
    const primary = replyToAddress(data)
    const parseList = (raw) => {
      if (!raw) return []
      return raw.split(',').map((s) => {
        const m = s.match(/<([^>]+)>/)
        return (m ? m[1] : s).trim()
      }).filter(Boolean)
    }
    const all = new Set([primary, ...parseList(headers['To']), ...parseList(headers['Cc'])])
    all.delete(self)
    all.delete('')
    return Array.from(all)
  }

  const openReply = (mode) => {
    const data = fullEmail || email
    const to = mode === 'replyAll'
      ? replyAllAddresses(data, address.email).join(', ')
      : replyToAddress(data)
    const subj = (data.subject || '').replace(/^(re:\s*)+/i, '').trim()
    setComposeMode(mode)
    setComposeTo(to)
    setComposeSubject(`Re: ${subj}`)
    // Quote the original body so the reply reads like a normal mail-client reply
    const quoted = (data.text_body || data.html_body?.replace(/<[^>]*>/g, '') || '')
      .split('\n').map((l) => `> ${l}`).join('\n')
    const dateLine = `On ${formatDate(data.received_at)}, ${data.sender} wrote:`
    setComposeBody(`\n\n${dateLine}\n${quoted}\n`)
    setSendError(null)
    setSendResult(null)
  }

  const closeReply = () => {
    setComposeMode(null)
    setComposeTo('')
    setComposeSubject('')
    setComposeBody('')
    setSendError(null)
    setSendResult(null)
  }

  const onSend = async () => {
    setSending(true)
    setSendError(null)
    try {
      const data = fullEmail || email
      const toList = composeTo.split(',').map((s) => s.trim()).filter(Boolean)
      const payload = {
        from_email: address.email,
        to: toList,
        subject: composeSubject,
        text_body: composeBody,
      }
      const msgId = data.headers?.['Message-ID']
      if (msgId) payload.in_reply_to = msgId
      const res = await api.sendEmail(payload)
      setSendResult(res)
    } catch (err) {
      setSendError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to emails
        </button>
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    )
  }

  const emailData = fullEmail || email

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to emails
      </button>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
          <h2 className="text-lg font-medium text-gray-900">
            {emailData.subject || '(No subject)'}
          </h2>
          <div className="flex shrink-0 gap-2">
            <button
              onClick={() => openReply('reply')}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply
            </button>
            <button
              onClick={() => openReply('replyAll')}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h11a4 4 0 014 4v1M17 8l4 4m0 0l-4 4m4-4H10" />
              </svg>
              Reply All
            </button>
          </div>
        </div>

        <div className="space-y-2 border-b border-gray-200 px-4 py-3 text-sm">
          <div className="flex">
            <span className="w-20 font-medium text-gray-500">From:</span>
            <span className="text-gray-900">{emailData.sender}</span>
          </div>
          <div className="flex">
            <span className="w-20 font-medium text-gray-500">To:</span>
            <span className="text-gray-900">{emailData.headers?.['To'] || address.email}</span>
          </div>
          {emailData.headers?.['Reply-To'] && (
            <div className="flex">
              <span className="w-20 font-medium text-gray-500">Reply-To:</span>
              <span className="font-mono text-emerald-700">{emailData.headers['Reply-To']}</span>
            </div>
          )}
          <div className="flex">
            <span className="w-20 font-medium text-gray-500">Date:</span>
            <span className="text-gray-900">{formatDate(emailData.received_at)}</span>
          </div>
        </div>

        {(emailData.html_body || emailData.text_body) && (
          <div className="p-4">
            {emailData.html_body && emailData.text_body && (
              <div className="mb-4 flex gap-2">
                <button
                  onClick={() => setShowHtml(true)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    showHtml
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  HTML
                </button>
                <button
                  onClick={() => setShowHtml(false)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    !showHtml
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Plain Text
                </button>
              </div>
            )}

            {showHtml && emailData.html_body ? (
              <div className="rounded-md border border-gray-200 bg-white p-4">
                <iframe
                  srcDoc={emailData.html_body}
                  title="Email content"
                  className="h-96 w-full border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <div className="whitespace-pre-wrap rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-800">
                {emailData.text_body || emailData.html_body?.replace(/<[^>]*>/g, '') || '(No content)'}
              </div>
            )}
          </div>
        )}

        {emailData.attachments && emailData.attachments.length > 0 && (
          <div className="border-t border-gray-200 px-4 py-3">
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Attachments ({emailData.attachments.length})
            </h3>
            <ul className="space-y-1">
              {emailData.attachments.map((attachment, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  {attachment.filename} ({attachment.content_type})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {composeMode && (
        <div className="overflow-hidden rounded-lg border border-blue-200 bg-white shadow-sm" data-testid="compose-panel">
          <div className="flex items-center justify-between border-b border-gray-200 bg-blue-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-blue-900">
              {composeMode === 'replyAll' ? 'Reply All' : 'Reply'}
            </h3>
            <button
              onClick={closeReply}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
          <div className="space-y-3 px-4 py-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-mono text-gray-800">
                {address.email}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="text"
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="compose-to"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
              <input
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="compose-subject"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={10}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="compose-body"
              />
            </div>
            {sendError && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                Send failed: {sendError}
              </div>
            )}
            {sendResult && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <div className="font-semibold">Sent ✓</div>
                <div className="mt-1 font-mono text-xs">
                  message_id: {sendResult.message_id}
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={closeReply}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                onClick={onSend}
                disabled={sending || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim() || !!sendResult}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="compose-send"
              >
                {sending ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                    Sending…
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailViewer
